import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAllUsedCatalogs } from "../../../../apis";
import { eqlCatalogueTree } from "../../../../apis/apiEQL";
import useLocalizedLabel from "../../../../hooks/useLocalizedLabel";
import {
  catalogRootKey,
  localizeCatalogValue,
  prettyCatalogValue,
} from "../utils/dimensions";

/**
 * Resolves a catalog dimension's stored value keys into localized labels.
 *
 * Two sources, because neither covers the whole problem alone:
 *
 *   getAllUsedCatalogs() — ONE request with every used catalog's FIRST-level
 *   options and their locales. The backend (ListLastFormCataloguesView) prunes
 *   the nested `type` on purpose, so this is all it can ever give.
 *
 *   eqlCatalogueTree(key) — the whole tree of ONE catalog, flattened and keyed
 *   by the stored value path. Fetched only for hierarchical catalogs, which are
 *   exactly the ones whose deep levels the first source cannot label (they used
 *   to render as the raw key, e.g. "Accounting_or_financial_irregularities").
 *
 * @param {Array} catalogDims - catalog dimensions in play; the hierarchical
 *   ones get their tree fetched once each, keyed by catalog alias.
 * @returns {{ catalogValueLabel: (dim, value) => string, labelsVersion: number }}
 *   catalogValueLabel localizes a value for a catalog dimension, falling back to
 *   prettyCatalogValue while its sources load. labelsVersion bumps every time a
 *   source lands, so callers can re-derive labels already rendered.
 */
const useCatalogValueLabels = (catalogDims = []) => {
  const [catalogs, setCatalogs] = useState(null);
  // { [catalogueKey]: { [valuePath]: { label, locales } } }
  const [trees, setTrees] = useState({});
  const getLocalizedLabel = useLocalizedLabel();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await getAllUsedCatalogs();
        if (active && resp) setCatalogs(resp);
      } catch (e) {
        console.error("Builder: could not load catalog value locales", e);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Alias list of the hierarchical catalogs, as a string so the effect below
  // does not re-run on every new array identity from the dims memo.
  const treeKeys = useMemo(
    () =>
      Array.from(
        new Set(
          (catalogDims || [])
            .filter((d) => d.hierarchical && d.catalogueKey)
            .map((d) => d.catalogueKey)
        )
      ).join("|"),
    [catalogDims]
  );

  // Trees already requested. A ref, not state: it must not retrigger the effect
  // that fills it, and a catalog is fetched once per mount even if the dims
  // list changes identity.
  const requested = useRef(new Set());

  useEffect(() => {
    if (!treeKeys) return;
    let active = true;

    const pending = treeKeys
      .split("|")
      .filter((key) => key && !requested.current.has(key));
    if (!pending.length) return;

    pending.forEach((key) => requested.current.add(key));

    (async () => {
      // Each catalog resolves on its own: one failing tree must not cost the
      // others their labels.
      await Promise.all(
        pending.map(async (key) => {
          try {
            const resp = await eqlCatalogueTree(key);
            if (!active || !resp?.nodes) return;
            if (resp.truncated) {
              console.warn(
                `Builder: catalog ${key} tree was truncated; deep values may fall back to their raw key`
              );
            }
            setTrees((prev) => ({ ...prev, [key]: resp.nodes }));
          } catch (e) {
            // Leave it requested: retrying on every render would hammer the
            // endpoint. Deep values keep the capitalized-key fallback.
            console.error(`Builder: could not load catalog tree ${key}`, e);
          }
        })
      );
    })();

    return () => {
      active = false;
    };
  }, [treeKeys]);

  const catalogValueLabel = useCallback(
    (dim, value) => {
      const options = catalogs?.[catalogRootKey(dim)]?.type;
      const nodes = trees[dim?.catalogueKey];
      return options || nodes
        ? localizeCatalogValue(value, options, getLocalizedLabel, nodes)
        : prettyCatalogValue(value);
    },
    [catalogs, trees, getLocalizedLabel]
  );

  const labelsVersion = (catalogs ? 1 : 0) + Object.keys(trees).length;

  return { catalogValueLabel, labelsVersion };
};

export default useCatalogValueLabels;
